import React, { useState } from 'react';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { ActionsDropdown } from 'components/common/ActionsDropdown/ActionsDropdown';
import { ConfirmDialog } from 'components/UI/ConfirmDialog/ConfirmDialog';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { EmailCampaign } from '../types';
import {
    CAMPAIGN_STATUS_LABELS,
    formatDate,
    formatNumber,
    formatPercent,
    formatRelative,
    locationNames
} from '../utils';
import { StatusPill } from '../UI/StatusPill/StatusPill';
import { buildCampaignActions, CAMPAIGN_STATUS_TONES } from './campaignActions';
import styles from './CampaignsTable.module.scss';

interface CampaignsTableProps {
    campaigns: Array<EmailCampaign>;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onOpen: (campaign: EmailCampaign) => void;
}

export const CampaignsTable: React.FC<CampaignsTableProps> = ({
    campaigns,
    page,
    totalPages,
    onPageChange,
    onOpen
}) => {
    const { duplicateCampaign, deleteCampaign, describeLocations, locations } = useEmailMarketing();
    const [toDelete, setToDelete] = useState<EmailCampaign | null>(null);

    return (
        <>
            <Table
                className={styles.table}
                pagination={{ page, totalPages, onPageChange }}
                firstColumnFixed
            >
                <TableHeader>
                    <TableHeaderCell>Campaign</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Locations</TableHeaderCell>
                    <TableHeaderCell className={styles.numeric}>Recipients</TableHeaderCell>
                    <TableHeaderCell className={styles.numeric}>Open rate</TableHeaderCell>
                    <TableHeaderCell className={styles.numeric}>Click rate</TableHeaderCell>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell />
                </TableHeader>

                <TableBody>
                    {campaigns.map(campaign => {
                        const names = locationNames(campaign.locationIds, locations);

                        return (
                            <TableRow
                                key={campaign.id}
                                className={styles.row}
                                onClick={() => onOpen(campaign)}
                            >
                                <TableCell className={styles.campaignCell}>
                                    <span className={styles.name}>{campaign.name}</span>
                                    <span className={styles.subject}>{campaign.subject || 'No subject yet'}</span>
                                </TableCell>
                                <TableCell>
                                    <StatusPill
                                        label={CAMPAIGN_STATUS_LABELS[campaign.status]}
                                        tone={CAMPAIGN_STATUS_TONES[campaign.status]}
                                    />
                                </TableCell>
                                <TableCell>
                                    {names.length > 1 && names.length < 10 ? (
                                        <Tooltip content={names.join(', ')}>
                                            <span className={styles.locations}>
                                                {describeLocations(campaign.locationIds)}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <span className={styles.locations}>
                                            {describeLocations(campaign.locationIds)}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className={styles.numeric}>
                                    {campaign.recipients ? formatNumber(campaign.recipients) : '—'}
                                </TableCell>
                                <TableCell className={styles.numeric}>{formatPercent(campaign.openRate)}</TableCell>
                                <TableCell className={styles.numeric}>{formatPercent(campaign.clickRate)}</TableCell>
                                <TableCell>
                                    {campaign.sentAt ? (
                                        <span>{formatDate(campaign.sentAt)}</span>
                                    ) : (
                                        <span className={styles.muted}>
                                            {campaign.updatedAt ? `Edited ${formatRelative(campaign.updatedAt)}` : '—'}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell isActions>
                                    <div onClick={event => event.stopPropagation()}>
                                        <ActionsDropdown
                                            direction="down"
                                            options={buildCampaignActions(campaign, {
                                                onOpen,
                                                onDuplicate: duplicateCampaign,
                                                onDelete: setToDelete
                                            })}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <ConfirmDialog
                open={toDelete !== null}
                title="Delete campaign"
                question={`Delete “${toDelete?.name}”?`}
                subquestion="This draft will be removed for every location. This can't be undone."
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Delete"
                onCancel={() => setToDelete(null)}
                onConfirm={() => {
                    if (toDelete) deleteCampaign(toDelete);
                    setToDelete(null);
                }}
            />
        </>
    );
};

export default CampaignsTable;
